import type { ReactNode } from "react";
import { useEffect, useReducer, createContext, useContext } from "react";
import Sentry from "@similarweb/sentry-logger";

import type { IShareUserExtended } from "app/sharing/SharingService";
import { getShareUsers } from "app/sharing/SharingService";
import { getDashboardViewersAndOwnerName } from "./helpers";
import type { IDashboardExtended } from "app/components/dashboard/services/IDashboard";

interface InitialState {
    users: IShareUserExtended[];
    isLoading: boolean;
    isError: boolean;
    viewers: IShareUserExtended[];
    dashboardOwnerName: string;
}

const initialState = {
    users: [],
    isLoading: false,
    isError: false,
    viewers: [],
    dashboardOwnerName: "",
};

const SharedUsersListReducer = (state: InitialState, action: Partial<InitialState>): InitialState => ({
    ...state,
    ...action,
});

const useSharedUsersListReducer = () => {
    return useReducer(SharedUsersListReducer, initialState);
};

const SharedUsersListContext = createContext<ReturnType<typeof useSharedUsersListReducer> | undefined>(undefined);

export const useSharedUsersListContext = () => {
    const ctx = useContext(SharedUsersListContext);

    if (!ctx) {
        throw Error("You should wrap in SharedUsersListProvider!");
    }

    return ctx;
};

export const SharedUsersListProvider = ({ children }: { children: ReactNode }) => {
    const [state, dispatch] = useSharedUsersListReducer();

    return <SharedUsersListContext.Provider value={[state, dispatch]}>{children}</SharedUsersListContext.Provider>;
};

export const useSharedUsersList = (currentDashboard: IDashboardExtended) => {
    const [state, dispatch] = useSharedUsersListContext();
    const { users, isLoading, isError, viewers, dashboardOwnerName } = state;

    useEffect(() => {
        const getUsersList = async () => {
            dispatch({ isLoading: true, isError: false });

            const usersIdsSharedWith = [
                ...currentDashboard?.sharedWithUsers,
                ...currentDashboard?.sharedWithInvitations,
            ];
            const isNewViewer = currentDashboard?.sharedWithInvitations.length > 0;

            if (users.length > 0 && !isNewViewer) {
                const { viewers, ownerName } = getDashboardViewersAndOwnerName(
                    usersIdsSharedWith,
                    users,
                    currentDashboard,
                    dashboardOwnerName,
                );
                dispatch({
                    viewers,
                    isLoading: false,
                    dashboardOwnerName: ownerName,
                });

                return;
            }

            try {
                const { users } = await getShareUsers();
                const usersExtended = users.map((user) => ({
                    ...user,
                    text:
                        Boolean(user.firstName) && Boolean(user.lastName)
                            ? `${user.firstName} ${user.lastName}`
                            : user.email,
                    isMyUser: false,
                    isValid: true,
                }));

                const { viewers, ownerName } = getDashboardViewersAndOwnerName(
                    usersIdsSharedWith,
                    usersExtended,
                    currentDashboard,
                    dashboardOwnerName,
                );

                dispatch({
                    users: usersExtended,
                    isLoading: false,
                    viewers,
                    dashboardOwnerName: ownerName,
                });
            } catch (error) {
                dispatch({ isLoading: false, isError: true });
                Sentry.error("error fetching share users", error);
            }
        };

        if (Boolean(currentDashboard)) {
            getUsersList();
        }
    }, [currentDashboard]);

    return { users, isLoading, isError, viewers, dashboardOwnerName };
};
